import { Loader, getSelectedSite, useUserSession } from "@platformx/utilities";
import { lazy, memo, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useAuthentication } from "../hooks/useAuthentication";
import { useDynamicRoutes } from "../hooks/useDynamicRoutes/useDynamicRoutes";
import { getCapturedOAuthCode } from "../utils/telemetrySecurity";

const LazyChangePassword = lazy(() =>
  import(`@platformx/user-management`).then((module) => ({
    default: module?.ChangePassword,
  })),
);

function RootRouter() {
  const location = useLocation();
  const [getSession] = useUserSession();
  const { userInfo } = getSession();
  const { handleSignIn, verifySession } = useAuthentication();
  const { generatedRoutes } = useDynamicRoutes();
  // Read from the capture store, not the URL: the code was stripped from the address
  // bar at app entry (before Sentry init) so Session Replay never records it (CWE-598).
  const code = getCapturedOAuthCode();

  useEffect(() => {
    const isLoggingOut = sessionStorage.getItem("isLoggingOut");
    if (!code && isLoggingOut !== "true") {
      verifySession();
    }
    // Clean up the flag after the effect runs (once per page load/location change)
    if (isLoggingOut === "true") {
      sessionStorage.removeItem("isLoggingOut");
    }
  }, [location]);

  useEffect(() => {
    if (code && Object.entries(userInfo || {}).length === 0) {
      handleSignIn(code);
    } else if (code && Object.entries(userInfo || {}).length !== 0) {
      const { selected_site } = userInfo;
      const lang = userInfo.preferred_sites_languages?.[selected_site] || "en";
      const redirectPath =
        selected_site?.toLowerCase() === "system" ? `sites/site-listing` : `dashboard`;
      window.location.replace(
        `${process.env.NX_PUBLIC_BASE_URL}/${selected_site}/${lang}/${redirectPath}`,
      );
    }
  }, []);
  const selectedSite = getSelectedSite();
  if (
    Object.entries(userInfo || {}).length < 1 ||
    userInfo?.selected_site !== selectedSite ||
    !generatedRoutes?.length
  ) {
    return <Loader />;
  }
  return (
    <Routes>
      <Route path='/change-password' element={<LazyChangePassword />} />
      {generatedRoutes?.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
    </Routes>
  );
}

export default memo(RootRouter);
